import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = selectedCategory === 'all'
    ? posts
    : posts.filter(p => p.category === selectedCategory);

  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="w-full" data-testid="blog-page">
      {/* Hero */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/1157386/pexels-photo-1157386.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <motion.div {...fadeInUp}>
            <h1 className="hero-title text-5xl sm:text-6xl lg:text-7xl text-white mb-6" data-testid="page-title">
              Blog & Stories
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Travel advice, Penticton lifestyle, and adventure inspiration
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-center gap-4 flex-wrap">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-6 py-2 rounded-full font-medium transition-colors ${
              selectedCategory === 'all'
                ? 'bg-[#1E6FBA] text-white'
                : 'bg-gray-200 text-[#4A4A4A] hover:bg-gray-300'
            }`}
            data-testid="filter-all"
          >
            All Posts
          </button>
          <button
            onClick={() => setSelectedCategory('penticton')}
            className={`px-6 py-2 rounded-full font-medium transition-colors ${
              selectedCategory === 'penticton'
                ? 'bg-[#1E6FBA] text-white'
                : 'bg-gray-200 text-[#4A4A4A] hover:bg-gray-300'
            }`}
            data-testid="filter-penticton"
          >
            Penticton
          </button>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="posts-section">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#1E6FBA]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-sm card-hover"
                data-testid={`post-${post.slug}`}
              >
                <div className="relative h-56">
                  <img
                    src={post.image_url}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-[#1E6FBA] text-white text-xs font-bold px-3 py-1 rounded-full uppercase">
                    {post.category}
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-[#757575] mb-3">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{post.published_at}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      <span>{post.author}</span>
                    </div>
                  </div>

                  <h3 className="text-xl mb-3 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {post.title}
                  </h3>

                  <p className="text-[#4A4A4A] mb-4 leading-relaxed line-clamp-3">
                    {post.excerpt}
                  </p>

                  <button className="inline-flex items-center text-[#1E6FBA] hover:text-[#F28A30] font-medium transition-colors" data-testid={`read-more-${post.slug}`}>
                    <span>Read More</span>
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Blog;
